import type { QuizAnswers, BusinessQuizAnswers, ConsumerQuizAnswers, ExpressInput } from '../types';

const GOAL_DESCRIPTIONS: Record<string, string> = {
  'save_emergency': 'build an emergency fund',
  'invest': 'start investing',
  'pay_debt': 'pay off debt',
  'retirement': 'plan for retirement',
  'budget': 'spend guilt-free while staying on track',
  'buy_home': 'save for a home',
  'family': 'provide financial security for my family',
  'travel': 'save for travel',
  'cash_flow': 'manage cash flow',
  'tax_planning': 'optimize for taxes',
  'growth': 'grow the business',
};

function formatGoals(goals: string[]): string {
  const descriptions = goals.map((g) => GOAL_DESCRIPTIONS[g] || g);

  if (descriptions.length === 0) return 'manage my finances better';
  if (descriptions.length === 1) return descriptions[0];
  if (descriptions.length === 2) return `${descriptions[0]} and ${descriptions[1]}`;

  const lastGoal = descriptions.pop();
  return `${descriptions.join(', ')}, and ${lastGoal}`;
}

function formatIncome(income: string): string {
  const incomeMap: Record<string, string> = {
    'under3k': 'under $3,000/month',
    '3k-5k': '$3,000-$5,000/month',
    '5k-8k': '$5,000-$8,000/month',
    '8k-12k': '$8,000-$12,000/month',
    '12k-20k': '$12,000-$20,000/month',
    'over20k': 'over $20,000/month',
    'under10k': 'under $10,000/month',
    '10k-25k': '$10,000-$25,000/month',
    '25k-50k': '$25,000-$50,000/month',
    '50k-100k': '$50,000-$100,000/month',
    '100k-250k': '$100,000-$250,000/month',
    'over250k': 'over $250,000/month',
  };
  return incomeMap[income] || income;
}

function formatDebtTypes(debtTypes: string[]): string {
  if (!debtTypes || debtTypes.length === 0) return 'various types of debt';

  const debtLabels: Record<string, string> = {
    'credit_card': 'credit card debt',
    'student_loans': 'student loans',
    'car_loan': 'a car loan',
    'mortgage': 'a mortgage',
    'personal_loan': 'personal loans',
    'medical': 'medical debt',
  };

  const labels = debtTypes.map((d) => debtLabels[d] || d);
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  const last = labels.pop();
  return `${labels.join(', ')}, and ${last}`;
}

export function generatePrompt(answers: Partial<QuizAnswers>): string {
  if (!answers.userType) {
    return 'Help me create a financial automation plan.';
  }

  const isBusinessUser = answers.userType === 'BUSINESS';
  const goals = formatGoals(answers.goals || []);

  if (isBusinessUser) {
    const business = answers as BusinessQuizAnswers;
    const parts: string[] = [];

    parts.push(`I run a ${business.industry || 'business'}`);

    if (business.monthlyRevenue) {
      parts.push(`with monthly revenue of ${formatIncome(business.monthlyRevenue)}`);
    }

    if (business.monthlyExpenses) {
      parts.push(`and monthly expenses around ${formatIncome(business.monthlyExpenses)}`);
    }

    if (business.hasDebt && business.debtTypes?.length) {
      parts.push(`I have business debt including: ${business.debtTypes.join(', ')}`);
    }

    parts.push(`My main goals are to ${goals}`);

    return parts.join('. ') + '. Create a comprehensive money flow automation plan for my business. Make sure to cover all essential financial areas: revenue allocation, operating expenses and bills, debt payments (if any), profit distribution, tax reserves, and business savings/emergency fund.';
  } else {
    const consumer = answers as ConsumerQuizAnswers;
    const parts: string[] = [];

    if (consumer.ageGroup) {
      parts.push(`I'm in my ${consumer.ageGroup.replace('-', 's (')})` || consumer.ageGroup);
    }

    if (consumer.relationshipStatus) {
      const statusMap: Record<string, string> = {
        'single': "I'm single",
        'married': "I'm married",
        'partnered': 'I have a partner',
      };
      if (statusMap[consumer.relationshipStatus]) {
        parts.push(statusMap[consumer.relationshipStatus]);
      }
    }

    if (consumer.dependentsStatus) {
      const dependentsMap: Record<string, string> = {
        'no_kids': 'with no kids',
        'kids_young': 'with young children',
        'kids_school': 'with school-age kids',
        'kids_adult': 'with adult children',
      };
      if (dependentsMap[consumer.dependentsStatus]) {
        parts.push(dependentsMap[consumer.dependentsStatus]);
      }
    }

    if (consumer.monthlyIncome) {
      parts.push(`I earn ${formatIncome(consumer.monthlyIncome)}`);
    }

    if (consumer.hasDebt && consumer.debtTypes?.length > 0) {
      parts.push(`I have ${formatDebtTypes(consumer.debtTypes)}`);
    }

    parts.push(`I want to ${goals}`);

    return parts.join('. ') + '. Create a personalized money flow automation plan. Make sure to cover all essential financial areas: income/salary allocation, monthly bills and expenses, debt payments (if any), emergency fund, savings goals, and investments/retirement contributions.';
  }
}

export function generateExpressPrompt(input: ExpressInput): string {
  let prompt = input.text;

  // Ensure minimum context
  if (prompt.length < 50 && !prompt.toLowerCase().includes('help')) {
    prompt = `Help me ${prompt.toLowerCase().startsWith('i want') ? prompt.slice(7) : prompt}`;
  }

  // Add user type context if detected
  if (input.userType === 'BUSINESS' && !prompt.toLowerCase().includes('business')) {
    prompt = `For my business: ${prompt}`;
  }

  // Add income context if provided
  if (input.incomeRange) {
    const incomeLabels: Record<string, string> = {
      'under50k': 'under $50,000/year',
      '50k-100k': '$50,000-$100,000/year',
      '100k-200k': '$100,000-$200,000/year',
      'over200k': 'over $200,000/year',
    };
    if (!prompt.toLowerCase().includes('income') && !prompt.toLowerCase().includes('earn')) {
      prompt += ` My income is ${incomeLabels[input.incomeRange] || input.incomeRange}.`;
    }
  }

  // Add comprehensive coverage instructions based on user type
  if (input.userType === 'BUSINESS') {
    prompt += ' Create a comprehensive money flow automation plan that covers all essential business financial areas: revenue allocation, operating expenses and bills, debt payments (if any), profit distribution, tax reserves, and business savings/emergency fund.';
  } else {
    prompt += ' Create a comprehensive money flow automation plan that covers all essential personal financial areas: income/salary allocation, monthly bills and expenses, debt payments (if any), emergency fund, savings goals, and investments/retirement contributions.';
  }

  return prompt;
}
